import { useEffect, useRef, useState } from "react";

/**
 * Basic drag-to-move hook. Attach `onPointerDown` to the drag handle
 * (e.g. the title bar) and apply `position` as a translate on the window.
 * Disabled entirely when `enabled` is false (e.g. while fullscreen).
 */
export default function useDraggable(initialPosition, enabled) {
  const [position, setPosition] = useState(initialPosition);
  const draggingRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });

  const onPointerDown = (e) => {
    if (!enabled) return;
    draggingRef.current = true;
    const point = e.touches ? e.touches[0] : e;
    offsetRef.current = { x: point.clientX - position.x, y: point.clientY - position.y };
  };

  useEffect(() => {
    if (!enabled) return;

    const onMove = (e) => {
      if (!draggingRef.current) return;
      const point = e.touches ? e.touches[0] : e;
      setPosition({
        x: point.clientX - offsetRef.current.x,
        y: point.clientY - offsetRef.current.y,
      });
    };
    const onUp = () => {
      draggingRef.current = false;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [enabled]);

  return { position, onPointerDown };
}